import React, { ReactNode, useEffect, useMemo } from 'react';
import {
  ConfidenceClient,
  ConfidenceClientContext,
  fallbackConfidenceClient,
  useConfidenceEvaluateFlag
} from 'context/ConfidenceReactAdapter';
import { useFirebaseAuth } from 'hooks/useFirebaseAuth';
import { isProduction } from 'lib/environment';
import useQuery from 'hooks/useUrlQuery';
import useWebview from 'hooks/useWebview';
import { useAnalytics } from 'hooks/useAnalytics';
import { getOrCreateVisitorId } from 'lib/firebase/ga_analytics';
import useTrackingConsent from 'hooks/useTrackingConsent';

const MockABTestFloatingMenu = React.lazy(
  () => import('components_v2024/ABTest/MockABTestFloatingMenu')
);

// --- Centralized test config ---
// isArchived: true means the flag is archived but we keep the logic in case we need to activate it again
// isHidden: true means the flag is hidden from the mock menu
export const AB_TESTS = [
  {
    name: 'Flag warmup', // used to pre-resolve confidence SDK
    flag: 'web-funnel-flag-warmup',
    variants: ['control'] as const,
    isHidden: true
  },
  {
    name: 'Retreats accommodation merge (E24)',
    flag: 'e24-retreats-accommodation-merge',
    variants: ['control', 'variant'] as const,
    isArchived: true
  },
  {
    name: 'Retreats hide follower count (E32)',
    flag: 'e32-retreats-hide-follower',
    variants: ['control', 'variant'] as const
  },
  {
    name: 'Retreats Early Bird Discount (E34)',
    flag: 'e34-retreats-early-bird-pricing',
    variants: ['control', 'variant-1', 'variant-2'] as const
  },
  {
    name: 'Retreat hide less 10 follower (E36)',
    flag: 'e36-retreats-hide-less-10-followers',
    variants: ['control', 'variant'] as const
  },
  {
    name: 'Retreats 24hr Refundable Deposits (E37)',
    flag: 'e37-retreats-24hr-refundable-deposits',
    variants: ['control', 'variant'] as const
  },
  {
    name: 'Sleep Landing Page on Web (E49)',
    flag: 'e49-sleep-landing-page-on-web',
    variants: ['control', 'variant'] as const
  }
  // Add new AB tests below, for example:
  // {
  //   name: 'New Feature Test',
  //   flag: 'new-feature-test',
  //   variants: ['a', 'b', 'c'] as const // 'a' will be the default variant
  // }
] as const;

type ABTestFlag = (typeof AB_TESTS)[number]['flag'];

export type Test = {
  [key in ABTestFlag]: (typeof AB_TESTS)[number]['variants'][number];
};

type Context = {
  testMock: Test | null;
  evaluatedTest: Test;
  loading: boolean;
  confidenceReady: boolean;
  suspendUntilReadyPromise?: Promise<void>;
};
const LOCAL_STORAGE_KEY = 'confidence-test-mock';

const ConfidenceContext = React.createContext<Context>({
  testMock: null,
  evaluatedTest: {} as Test,
  loading: true,
  confidenceReady: false,
  suspendUntilReadyPromise: undefined
});

export const ABTestProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useFirebaseAuth();
  const visitorId = useMemo(() => getOrCreateVisitorId(), []);
  const { isTrackingConsented } = useTrackingConsent();
  const [testMock, setTestMock] = React.useState<null | Test>(null);
  const evaluatedFlags = React.useRef<Test>({} as Test);
  const [confidenceClient, setConfidenceClient] =
    React.useState<ConfidenceClient>(fallbackConfidenceClient);
  const [confidenceClientLoaded, setConfidenceClientLoaded] =
    React.useState(false);
  const [confidenceReady, setConfidenceReady] = React.useState(false);
  const { isRetreatWebview } = useWebview();
  const searchUrl = useQuery();
  const userIdFromUrl = isRetreatWebview ? searchUrl.get('user-id') : null;

  const loading = visitorId === undefined;
  const isABTestReady = !loading && confidenceReady;
  const suspendUntilReadyRef = React.useRef<{
    promise?: Promise<void>;
    resolve?: () => void;
  }>({});

  // Load test mock from local storage on mount if tracking is consented, and clear it if not consented 
  useEffect(() => {
    if(!isTrackingConsented) { 
      localStorage.removeItem(LOCAL_STORAGE_KEY);
      setTestMock(null);
      return;
    }

    const storedTestMock = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (storedTestMock) {
      try {
        const parsedTestMock = JSON.parse(storedTestMock);
        setTestMock(parsedTestMock);
      } catch (error) {
        console.error('Error parsing test mock from local storage:', error);
      }
    }
  }, [isTrackingConsented]);

  const handleSetTestMock = (test: Test | null) => {
    setTestMock(test);
    if (test === null) {
      localStorage.removeItem(LOCAL_STORAGE_KEY);
    } else {
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(test));
    }
  };

  useEffect(() => {
    let mounted = true;
    import('@spotify-confidence/sdk').then(({ Confidence }) => {
      if (!mounted) return;
      const client = Confidence.create({
        clientSecret: process.env.REACT_APP_CONFIDENCE_CLIENT_SECRET ?? '',
        environment: 'client',
        timeout: 3000
      });
      setConfidenceClient(client as unknown as ConfidenceClient);
      setConfidenceClientLoaded(true);
    });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!confidenceClientLoaded || visitorId === undefined) {
      setConfidenceReady(false);
      return;
    }

    confidenceClient.clearContext();

    confidenceClient.setContext({
      visitor_id: visitorId,
      user_id: user?.uid ?? userIdFromUrl ?? undefined
    });

    setConfidenceReady(true);
  }, [
    visitorId,
    user,
    userIdFromUrl,
    confidenceClient,
    confidenceClientLoaded
  ]);

  if (!isABTestReady && !suspendUntilReadyRef.current.promise) {
    suspendUntilReadyRef.current.promise = new Promise<void>(resolve => {
      suspendUntilReadyRef.current.resolve = resolve;
    });
  }

  useEffect(() => {
    if (!isABTestReady || !suspendUntilReadyRef.current.resolve) {
      return;
    }

    // Resolve the promise to unsuspend any components waiting for AB test readiness
    suspendUntilReadyRef.current.resolve();
    suspendUntilReadyRef.current.promise = undefined;
    suspendUntilReadyRef.current.resolve = undefined;
  }, [isABTestReady]);

  const providerValue = {
    evaluatedTest: evaluatedFlags.current,
    testMock,
    loading,
    confidenceReady,
    suspendUntilReadyPromise: isABTestReady
      ? undefined
      : suspendUntilReadyRef.current.promise
  };

  const contextNode = (
    <ConfidenceContext.Provider value={providerValue}>
      {children}
      {!isProduction && (
        <React.Suspense fallback={null}>
          <MockABTestFloatingMenu
            testMock={testMock}
            setTestMock={handleSetTestMock}
          />
        </React.Suspense>
      )}
    </ConfidenceContext.Provider>
  );

  return (
    <ConfidenceClientContext.Provider value={confidenceClient}>
      {contextNode}
    </ConfidenceClientContext.Provider>
  );
};

const useABTest = () => React.useContext(ConfidenceContext);

const lastLoggedFlagVariant: Record<string, string> = {};

export function useABTestFlag(flag: ABTestFlag) {
  const { testMock, suspendUntilReadyPromise } =
    useABTest();
  const { isTrackingConsented } = useTrackingConsent();
  const { trackGAExperimentActivated } = useAnalytics();
  const testConfig = AB_TESTS.find(t => t.flag === flag)!;
  const defaultVariant = testConfig.variants[0];

  // Suspend until requirements are met for this flag
  if (isTrackingConsented && suspendUntilReadyPromise) {
    throw suspendUntilReadyPromise;
  }

  const result = useConfidenceEvaluateFlag(
    flag,
    { variant: defaultVariant },
    { enabled: isTrackingConsented && !suspendUntilReadyPromise }
  );

  if (!isTrackingConsented) {
    return defaultVariant;
  }

  let variant =
    typeof result.value === 'object' &&
    result.value !== null &&
    'variant' in result.value
      ? (result.value as { variant: (typeof testConfig)['variants'][number] })
          .variant
      : defaultVariant;

  if (testMock && testMock[flag]) {
    console.info(`Using mock AB test for flag: ${flag}`, testMock[flag]);
    variant = testMock[flag];
  }

  // Only log if flag/variant combination has changed globally
  const currentVariant = variant;
  if (isTrackingConsented && lastLoggedFlagVariant[flag] !== currentVariant) {
    trackGAExperimentActivated({
      experiment_id: flag,
      variation_id: currentVariant
    });
    lastLoggedFlagVariant[flag] = currentVariant;
  }

  return variant;
}
